import { m } from "@/paraglide/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { debounce } from "lodash-es";
import { useState } from "react";
import { FilterIcon, GemIcon2 } from "~/components/icons";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import Nodata from "~/components/ui/no-data";
import { PaginationPro } from "~/components/ui/pagination";
import { type Column } from "~/components/ui/table-pro";
import { BaseResponse } from "~/lib/request";
import { formatThousands, formatTimestampAuto } from "~/lib/utils";
import { OrderHistoryResponse, orderHistory } from "./-api";

export const Route = createFileRoute("/$lang/profile/deposit")({
	component: ProfileDeposit,
});

const allChannels = [
	{
		label: m.profile_deposit_channel_credit_card(),
		value: "PaymentChannelStripe",
	},
	{
		label: m.profile_deposit_channel_crypto(),
		value: "PaymentChannelWeb3",
	},
];
const PAGE_SIZE = 10;

function ProfileDeposit() {
	const [channelFilter, setChannelFilter] = useState<string[]>(
		allChannels.map((ch) => ch.value),
	);

	const [page, setPage] = useState(1);

	const { data, isLoading } = useQuery<BaseResponse<OrderHistoryResponse>>({
		queryKey: ["orderHistory", channelFilter, page],
		queryFn: () =>
			orderHistory({
				page_num: page,
				page_size: PAGE_SIZE,
				payment_channel: channelFilter.join(","),
			}),
	});

	const records: OrderHistoryResponse["list"] = data?.data?.list ?? [];
	const total: number = data?.data?.total ?? 0;
	const pageCount = Math.ceil(total / PAGE_SIZE);

	type RecordType = (typeof records)[number];
	const columns: Column<RecordType>[] = [
		{
			key: "amount",
			title: m.profile_deposit_amount(),
			render: (v: number, row: RecordType) => (
				<span className="max-w-20 md:max-w-full truncate">
					{row.currency_symbol}
					{formatThousands(v)}
				</span>
			),
		},
		{
			key: "gem",
			title: m.profile_deposit_gem(),
			render: (v: number) => <span>{formatThousands(v)}</span>,
		},
		{
			key: "payment_channel",
			title: (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className="flex items-center justify-center cursor-pointer gap-1 font-medium bg-transparent outline-none"
							style={{ minWidth: 80 }}
						>
							{m.profile_deposit_channel()} <FilterIcon />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="center"
						className="w-[162px] p-0 border-0"
					>
						{allChannels.map((ch) => (
							<DropdownMenuCheckboxItem
								key={ch.value}
								checked={channelFilter.includes(ch.value)}
								onCheckedChange={(checked) => {
									setChannelFilter((prev) => {
										const next = checked
											? [...prev, ch.value]
											: prev.filter((c) => c !== ch.value);
										setPage(1);
										return next;
									});
								}}
								className="flex items-center gap-2 border-b border-line1 rounded-none py-2"
							>
								<span className="text-sm">{ch.label}</span>
							</DropdownMenuCheckboxItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			),
			render: (v: string) => (
				<span className="">
					{allChannels.find((ch) => ch.value === v)?.label}
				</span>
			),
		},
		{
			key: "created_time",
			title: m.profile_deposit_time(),
			render: (v: number) => <span>{formatTimestampAuto(v)}</span>,
		},
	];

	return (
		<div className="px-[18px] py-[22px] pb-0">
			<div className="grid grid-cols-12 justify-between items-center text-t3 pb-5 text-sm font-medium border-b border-line1">
				{columns.map((col, index) => (
					<div
						key={String(col.key)}
						className={"col-span-3 flex justify-center"}
					>
						{col.title}
					</div>
				))}
			</div>
			<div className="min-h-[500px]">
				{records.map((row: RecordType, index: number) => (
					<div
						key={row.order_id + index}
						className="grid grid-cols-12 justify-between items-center text-t1 text-sm font-medium border-b border-line1 last:border-b-0"
					>
						{columns.map((col, index) => (
							<div
								key={String(col.key)}
								className={
									"col-span-3 flex h-10 items-center gap-[6px] justify-center"
								}
							>
								{col.render
									? col.render(row[col.key as keyof RecordType], row)
									: (row[col.key as keyof RecordType] ?? "-")}
								{col.key === "gem" && (
									<GemIcon2 className="w-[18px] h-[18px]" />
								)}
							</div>
						))}
					</div>
				))}
				{isLoading && (
					<div className="text-center text-t3 text-sm py-6">
						<m.common_loading />
					</div>
				)}
				{records.length === 0 && !isLoading && <Nodata />}
				{pageCount >= 1 && (
					<div className="flex justify-center py-4">
						<PaginationPro
							page={page}
							pageCount={pageCount}
							onPageChange={setPage}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
